<template>
    <BModal
        :model-value="modal"
        centered
        @update:modelValue="updateModal"
        scrollable
        no-close-on-backdrop
    >
        <template #header>
            <div
                class="d-flex align-items-center justify-content-between modal-title w-100"
            >
                <h5 class="">Meal</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <BForm @submit.stop.prevent>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Name *"
                        label-for="name"
                        class="text-start mb-3"
                    >
                        <BFormSelect
                            v-model="form.name"
                            id="name"
                            :options="mealOptions"
                            placeholder="Select Name"
                            :class="{
                                'is-invalid':
                                    v$.form.name.$dirty &&
                                    v$.form.name.$invalid,
                            }"
                        >
                        </BFormSelect>
                        <BFormInvalidFeedback
                            :state="
                                v$.form.name.$dirty && v$.form.name.$invalid
                            "
                        >
                            {{ v$.form.name.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Description"
                        label-for="description"
                        class="text-start mb-3"
                    >
                        <BFormTextarea
                            v-model="form.description"
                            id="description"
                            placeholder="Enter Description"
                            rows="3"
                            max-rows="6"
                        />
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Pax *"
                        label-for="pax"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.pax"
                            type="number"
                            id="pax"
                            placeholder="Enter Pax"
                            @update:modelValue="calculateSubtotal"
                            min="1"
                            :class="{
                                'is-invalid':
                                    v$.form.pax.$dirty &&
                                    v$.form.pax.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.pax.$dirty && v$.form.pax.$invalid
                            "
                        >
                            {{ v$.form.pax.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
                <BCol>
                    <BFormGroup
                        label="Price per Pax *"
                        label-for="price"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.price"
                            type="number"
                            id="price"
                            placeholder="Enter Price"
                            @update:modelValue="calculateSubtotal"
                            min="1"
                            :class="{
                                'is-invalid':
                                    v$.form.price.$dirty &&
                                    v$.form.price.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.price.$dirty && v$.form.price.$invalid
                            "
                        >
                            {{ v$.form.price.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Subtotal *"
                        label-for="sub_total"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.sub_total"
                            type="number"
                            id="sub_total"
                            :class="{
                                'is-invalid':
                                    v$.form.sub_total.$dirty &&
                                    v$.form.sub_total.$invalid,
                            }"
                            placeholder="Subtotal"
                            @update:modelValue="calculatePrice"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.sub_total.$dirty && v$.form.sub_total.$invalid
                            "
                        >
                            {{ v$.form.sub_total.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
        </BForm>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="primary" class="me-2" @click="handleAction"
                    >Save</BButton
                >
                <BButton variant="danger" @click="updateModal(false)"
                    >Cancel</BButton
                >
            </div>
        </template>
    </BModal>
</template>

<script setup>
import { defineProps, defineEmits, reactive, watch, ref } from "vue";
import {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BButton,
    BFormGroup,
    BFormInvalidFeedback,
    BFormSelect,
    BFormTextarea,
} from "bootstrap-vue-next";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, minValue } from "@vuelidate/validators";
import { useLoading } from "vue-loading-overlay";
import { useToast } from "vue-toastification";
const $loading = useLoading();
const toast = useToast();

// Props
const props = defineProps({
    action: String,
    modal: Boolean,
    selectedData: Object,
    defaultPax: Number,
});

// Emit
const emit = defineEmits(["update:modal", "addOrUpdate"]);

// Function to update modal visibility
const updateModal = (value) => {
    selectedTransportation.value = null;
    emit("update:modal", value); // Sync modal state with parent
};

const resetForm = () => {
    Object.assign(form, { ...formData });
    v$.value.$reset();
};

let selectedTransportation = ref(null);
const formData = {
    uuid: "",
    name: "",
    description: "",
    pax: "",
    price: "",
    sub_total: "",
};

const form = reactive({ ...formData });

// Init data jika update
watch(
    () => props.selectedData,
    (newData) => {
        if (newData && Object.keys(newData).length > 0) {
            const transportationData = {
                ...newData,
            };
            Object.assign(form, { ...transportationData });
        } else {
            Object.assign(form, { ...formData, pax: props.defaultPax || "1" });
            // Initial calculation if needed
            if(form.pax && form.price) calculateSubtotal();
        }
    },
    { immediate: true }
);

// Watch for defaultPax changes from parent (formProgram.pax)
watch(
    () => props.defaultPax,
    (newPax) => {
        // Only update if not editing existing data and pax is different
        if ((!props.selectedData || Object.keys(props.selectedData).length === 0) && newPax) {
            form.pax = newPax;
            calculateSubtotal();
        }
    }
);

const rules = {
    form: {
        name: {
            required: helpers.withMessage("Name is required", required),
        },
        price: {
            required: helpers.withMessage("Price is required", required),
            minValue: helpers.withMessage(
                "Price must be at least 1",
                minValue(1)
            ),
        },
        pax: {
            required: helpers.withMessage("Pax is required", required),
            minValue: helpers.withMessage(
                "Pax must be at least 1",
                minValue(1)
            ),
        },
        sub_total: {
            required: helpers.withMessage("Subtotal is required", required),
            minValue: helpers.withMessage(
                "Subtotal must be at least 1",
                minValue(1)
            ),
        },
        inclusion: {},
        exclusion: {},
    },
};

const v$ = useVuelidate(rules, { form });

const handleAction = () => {
    props.selectedData.index != undefined ? update() : create();
};

const create = async () => {
    toast.clear();
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({
            color: "#489464",
            loader: "spinner",
        });

        try {
            // Jka ada index hapus, index hanya jika edit
            const payload = { ...form };
            delete payload.index;
            emit("addOrUpdate", payload);
            toast.success("Meal added successfully");
            updateModal(false);
            resetForm();
        } finally {
            loader.hide();
        }
    }
};

const update = async () => {
    toast.clear();
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({
            color: "#489464",
            loader: "spinner",
        });

        try {
            emit("addOrUpdate", {
                index: props.selectedData.index,
                ...form,
            });
            toast.success("Meal updated successfully");
            updateModal(false);
            resetForm();
        } finally {
            loader.hide();
        }
    }
};

// Meal Options
const mealOptions = [
    {value: "", text: 'Select Name', disabled: true},
    {
        value: "Lunch",
        text: "Lunch",
    },
    {
        value: "Dinner",
        text: "Dinner",
    },
];

const calculateSubtotal = () => {
    if (form.pax && form.price) {
        form.sub_total = form.pax * form.price;
    } else {
        if (!form.pax || !form.price) {
            form.sub_total = 0;
        }
    }
};

const calculatePrice = () => {
    if (form.pax && form.pax != 0 && form.sub_total) {
        form.price =   Math.round(form.sub_total / form.pax);
    } else if (form.pax == 0 && form.sub_total) {
        // can't divide by zero
    } else {
        if (!form.sub_total) {
            form.price = 0;
        }
    }
};
</script>
